import React, { useState, useEffect, useContext, useMemo } from 'react'
import {
  Radio, Input, Form, Select, Divider, DatePicker, TimePicker, Upload, message, Spin
} from 'antd'
import dayjs from 'dayjs'
import { PlusOutlined, InboxOutlined, DeleteOutlined } from '@ant-design/icons'
import { useMsal } from '@azure/msal-react'
import { nanoid } from 'nanoid'

import Modal from '../../../../components/common/modal/modal'
import Button from '../../../../components/common/button/button'
import {
  createTransaction,
  formatTransactionData,
  fetchCashFlowTypes
} from '../../../../api/transactionsApiService/transactionsApiUtils'
import { UserContext } from '../../../../context/userContext'
import { StoreContext } from '../../../../context/storeContext'
import './transactionModal.scss'

const { TextArea } = Input
const { Option } = Select
const { Dragger } = Upload

const INITIAL_STATE = {
  transactionType: 'out',
  amount: '',
  invoiceNumber: '',
  notes: '',
  fileList: [],
  transactionDate: dayjs(),
  transactionTime: dayjs(),
  selectedType: null,
  amountError: '',
  formErrors: {},
  submitting: false,
  formSubmitted: false
}

// Helper function to sanitize file names (remove spaces)
const sanitizeFileName = (file) => {
  if (!file || !file.name) return file

  // Create a new file object with sanitized name (replacing spaces with underscores)
  const newName = file.name.replace(/\s+/g, '_')

  // If the name hasn't changed, return the original file
  if (newName === file.name) return file

  // Create a new File object with the sanitized name
  const sanitizedFile = new File([file], newName, { type: file.type })

  // Copy over any additional properties that might be used by antd Upload
  Object.keys(file).forEach(key => {
    if (key !== 'name') {
      sanitizedFile[key] = file[key]
    }
  })

  return sanitizedFile
}

const TransactionModal = ({ visible, onClose, currentBalance = '4050.00', onSuccess, storeId: propStoreId = '101' }) => {
  // Form state
  const [transactionType, setTransactionType] = useState(INITIAL_STATE.transactionType)
  const [amount, setAmount] = useState(INITIAL_STATE.amount)
  const [invoiceNumber, setInvoiceNumber] = useState(INITIAL_STATE.invoiceNumber)
  const [invoiceNumberError, setInvoiceNumberError] = useState('')
  const [notes, setNotes] = useState(INITIAL_STATE.notes)
  const [fileList, setFileList] = useState(INITIAL_STATE.fileList)
  const [transactionDate, setTransactionDate] = useState(INITIAL_STATE.transactionDate)
  const [transactionTime, setTransactionTime] = useState(INITIAL_STATE.transactionTime)
  const [submitting, setSubmitting] = useState(INITIAL_STATE.submitting)
  const [formSubmitted, setFormSubmitted] = useState(INITIAL_STATE.formSubmitted)

  // Field-specific state
  const [newEntryText, setNewEntryText] = useState('')
  const [showAddNew, setShowAddNew] = useState(false)
  const [selectedType, setSelectedType] = useState(INITIAL_STATE.selectedType)
  const [amountError, setAmountError] = useState(INITIAL_STATE.amountError)
  const [formErrors, setFormErrors] = useState(INITIAL_STATE.formErrors)

  // Data state
  const [expenseTypes, setExpenseTypes] = useState([])
  const [depositTypes, setDepositTypes] = useState([])
  const [loading, setLoading] = useState(false)

  // Contexts and hooks
  const userContext = useContext(UserContext)
  const storeContext = useContext(StoreContext)
  const { instance } = useMsal()
  const [form] = Form.useForm()

  // Store ID resolution - simplified to use StoreContext directly
  const effectiveStoreId = useMemo(() => {
    // Primary: Use store ID from StoreContext
    if (storeContext?.id) {
      return storeContext.id
    }

    // Fallback: Use provided prop or default
    return propStoreId || '101'
  }, [storeContext?.id, propStoreId])

  // Reset form when modal opens
  useEffect(() => {
    if (visible) {
      resetForm()
      fetchTypeOptions()
    }
  }, [visible])

  // Validate amount against balance and cap when transaction type or amount changes
  useEffect(() => {
    if (transactionType === 'out' && amount && !isNaN(parseFloat(amount))) {
      const amountValue = parseFloat(amount)

      if (amountValue > 1000) {
        setAmountError('Cash out amount cannot exceed PHP 1000')
      } else if (amountValue > parseFloat(currentBalance)) {
        setAmountError(`Amount exceeds current balance (PHP ${currentBalance})`)
      } else if (amountValue === 0) {
        setAmountError('Amount must be greater than 0')
      } else if (amountError.includes('exceeds current balance') ||
        amountError.includes('must be greater than 0') ||
        amountError.includes('cannot exceed PHP 1000')) {
        // Clear the error only if it was a balance error, zero amount error, or cap error
        setAmountError('')
      }
    }
  }, [transactionType, amount, currentBalance, amountError])

  // Reset all form fields to initial values
  const resetForm = () => {
    // Reset all form state variables to initial values
    Object.entries(INITIAL_STATE).forEach(([key, value]) => {
      // Use the appropriate setter function based on the key
      if (key === 'transactionType') setTransactionType(value)
      else if (key === 'amount') setAmount(value)
      else if (key === 'invoiceNumber') setInvoiceNumber(value)
      else if (key === 'notes') setNotes(value)
      else if (key === 'fileList') setFileList(value)
      else if (key === 'transactionDate') setTransactionDate(value)
      else if (key === 'transactionTime') setTransactionTime(value)
      else if (key === 'selectedType') setSelectedType(value)
      else if (key === 'amountError') setAmountError(value)
      else if (key === 'formErrors') setFormErrors(value)
      else if (key === 'submitting') setSubmitting(value)
      else if (key === 'formSubmitted') setFormSubmitted(value)
    })

    // Reset the Ant Design form
    form.resetFields()

    // Reset UI state
    setNewEntryText('')
    setShowAddNew(false)
  }

  // Fetch transaction types
  const fetchTypeOptions = async () => {
    try {
      setLoading(true)
      const msalInstance = userContext?.msalInstance || instance
      const response = await fetchCashFlowTypes(msalInstance)

      // Process the API response to extract types
      let cashOut = []
      let cashIn = []

      // Handle various response formats
      if (response?.data?.pettyCashGenericSettings) {
        cashOut = response.data.pettyCashGenericSettings.cashOut || []
        cashIn = response.data.pettyCashGenericSettings.cashIn || []
      } else if (response?.pettyCashGenericSettings) {
        cashOut = response.pettyCashGenericSettings.cashOut || []
        cashIn = response.pettyCashGenericSettings.cashIn || []
      } else if (Array.isArray(response?.data)) {
        const types = response.data.map(item =>
          typeof item === 'string' ? item : item.name || item.value || item.type
        )
        cashOut = cashIn = types
      }

      // Ensure unique values
      setExpenseTypes([...new Set(cashOut)])
      setDepositTypes([...new Set(cashIn)])
    } catch (error) {
      console.error('Error fetching cash flow types:', error)
      // Keep empty arrays when API fails
      setExpenseTypes([])
      setDepositTypes([])
    } finally {
      setLoading(false)
    }
  }

  // Form validation
  const validateForm = () => {
    setFormSubmitted(true)
    const errors = {}

    // Validate required fields
    if (!transactionDate) errors.date = true
    if (!transactionTime) errors.time = true
    if (!selectedType) errors[transactionType === 'out' ? 'expenseType' : 'depositType'] = true
    if (!amount || amount.trim() === '') errors.amount = true

    // Check for mandatory invoice number for cash out transactions only
    if (transactionType === 'out' && (!invoiceNumber || invoiceNumber.trim() === '')) {
      errors.invoiceNumber = true
    }

    // Check for mandatory description
    if (!notes || notes.trim() === '') {
      errors.notes = true
    }

    // Check for zero amount regardless of transaction type
    if (amount && !isNaN(parseFloat(amount)) && parseFloat(amount) === 0) {
      errors.zeroAmount = true
      setAmountError('Amount must be greater than 0')
    }

    // Add PHP 1000 cap validation for cash-out transactions
    if (transactionType === 'out' && amount && !isNaN(parseFloat(amount)) && parseFloat(amount) > 1000) {
      errors.capExceeded = true
      setAmountError('Cash out amount cannot exceed PHP 1000')
    }

    // Add balance validation only for cash-out transactions
    if (transactionType === 'out' && amount && !isNaN(parseFloat(amount)) && parseFloat(amount) > parseFloat(currentBalance)) {
      errors.balanceExceeded = true
      setAmountError(`Amount exceeds current balance (PHP ${currentBalance})`)
    }

    // Check invoice number format if it's not empty
    if (invoiceNumber && invoiceNumber.trim() !== '' && !/^[a-zA-Z0-9-_.]*$/.test(invoiceNumber)) {
      errors.invoiceFormat = true
    }

    // Update form errors state
    setFormErrors(errors)

    // Return false if there are any errors or if there are other validation errors not tracked in formErrors
    return Object.keys(errors).length === 0 && amountError === '' && invoiceNumberError === ''
  }

  // Handle form field changes
  const handleTypeChange = (value) => {
    setSelectedType(value)
    if (formSubmitted) {
      setFormErrors({ ...formErrors, expenseType: false, depositType: false })
    }
  }

  const handleAmountChange = (e) => {
    const value = e.target.value

    // Only allow numeric with two decimal places
    if (value && (!/^[\d.]*$/.test(value) || !/^\d*\.?\d{0,2}$/.test(value))) {
      return
    }

    // Check digit length limit for cash in transactions (7 digits before decimal)
    if (transactionType === 'in' && value) {
      const beforeDecimal = value.split('.')[0]
      if (beforeDecimal.length > 7) {
        return
      }
    }

    setAmount(value)

    if (formSubmitted) {
      if (!value || value.trim() === '') {
        setFormErrors({ ...formErrors, amount: true })
      } else {
        setFormErrors({ ...formErrors, amount: false, balanceExceeded: false, zeroAmount: false, capExceeded: false })
      }
    }

    // Always check for validation cases
    if (value && !isNaN(parseFloat(value))) {
      const amountValue = parseFloat(value)

      if (amountValue === 0) {
        setAmountError('Amount must be greater than 0')
      } else if (transactionType === 'out' && amountValue > 1000) {
        setAmountError('Cash out amount cannot exceed PHP 1000')
      } else if (transactionType === 'out' && amountValue > parseFloat(currentBalance)) {
        setAmountError(`Amount exceeds current balance (PHP ${currentBalance})`)
      } else {
        setAmountError('')
      }
    } else if (value === '' && amountError !== '' &&
      !amountError.includes('exceeds current balance') &&
      !amountError.includes('must be greater than 0') &&
      !amountError.includes('cannot exceed PHP 1000')) {
      // Clear non-validation related errors
      setAmountError('')
    }
  }

  const handleTransactionTypeChange = (e) => {
    const newType = e.target.value
    setTransactionType(newType)
    setSelectedType(null)

    // When changing transaction types, preserve amount but recalculate validation
    if (!amountError.includes('exceeds current balance') &&
      !amountError.includes('must be greater than 0') &&
      !amountError.includes('cannot exceed PHP 1000')) {
      setAmountError('')
    }

    // Only update form errors if form has been submitted
    if (formSubmitted) {
      setFormErrors({
        ...formErrors,
        expenseType: false,
        depositType: false,
        balanceExceeded: false,
        zeroAmount: false,
        capExceeded: false,
        // Keep amount error status if the field is empty
        amount: !amount || amount.trim() === '',
        // Reset invoice number error since requirement changes based on transaction type
        invoiceNumber: false
      })
    }

    // Check for validation cases regardless of transaction type
    if (amount && !isNaN(parseFloat(amount))) {
      const amountValue = parseFloat(amount)

      if (amountValue === 0) {
        setAmountError('Amount must be greater than 0')
        if (formSubmitted) {
          setFormErrors(prev => ({ ...prev, zeroAmount: true }))
        }
      } else if (newType === 'out' && amountValue > 1000) {
        setAmountError('Cash out amount cannot exceed PHP 1000')
        if (formSubmitted) {
          setFormErrors(prev => ({ ...prev, capExceeded: true }))
        }
      } else if (newType === 'out' && amountValue > parseFloat(currentBalance)) {
        // Only check balance for cash-out
        setAmountError(`Amount exceeds current balance (PHP ${currentBalance})`)
        if (formSubmitted) {
          setFormErrors(prev => ({ ...prev, balanceExceeded: true }))
        }
      }
    }
  }

  const handleDateChange = (date) => {
    setTransactionDate(date)
    // Only clear error if form has been submitted
    if (formSubmitted) {
      setFormErrors({ ...formErrors, date: false })
    }

    // Reset time if needed
    if (date && date.format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD')) {
      if (transactionTime && transactionTime > dayjs()) {
        setTransactionTime(dayjs())
      }
    }
  }

  // Date & time constraints
  const disabledDate = (current) => {
    const today = dayjs()
    return (
      current.month() !== today.month() ||
      current.year() !== today.year() ||
      current.isAfter(today, 'day')
    )
  }

  const disabledTime = (now) => {
    const today = dayjs()
    const selectedDate = transactionDate || today

    if (selectedDate.isSame(today, 'day')) {
      const hours = []
      const minutes = []

      // Disable future hours
      for (let i = today.hour() + 1; i < 24; i++) {
        hours.push(i)
      }

      // Disable future minutes if the selected hour is the current hour
      if (now && now.hour() === today.hour()) {
        for (let i = today.minute() + 1; i < 60; i++) {
          minutes.push(i)
        }
      }

      return {
        disabledHours: () => hours,
        disabledMinutes: (selectedHour) => selectedHour === today.hour() ? minutes : []
      }
    }

    return {}
  }

  // Handle attachments with filename sanitization (spaces removal)
  const handleFileChange = ({ fileList }) => {
    // First sanitize the filenames (remove spaces)
    const sanitizedFileList = fileList.map(file => {
      // Only sanitize if it's a file object with a name
      if (file.originFileObj) {
        file.originFileObj = sanitizeFileName(file.originFileObj)

        // Also update the name property in the fileList item to match
        if (file.originFileObj.name !== file.name) {
          file.name = file.originFileObj.name
        }
      }
      return file
    })

    // Then filter for valid file types
    const filteredFiles = sanitizedFileList.filter(file => {
      const isImage = file.type?.startsWith('image/')
      const isPdf = file.type === 'application/pdf'
      const isValidFile = isImage || isPdf

      if (!isValidFile && file.type) {
        message.error(`${file.name} is not an image or PDF file`)
      }

      return isValidFile || !file.type
    })

    setFileList(filteredFiles)

    // Notify user about filename changes if spaces were removed
    const filesWithSpaces = fileList.filter(file =>
      file.name && file.name.includes(' ')
    )

    if (filesWithSpaces.length > 0) {
    }
  }

  // Handle new type entries
  const handleAddNewType = () => {
    if (!newEntryText) return

    // Limit expense type and deposit type length to 25 characters
    if (newEntryText.length > 25) {
      message.error(`${transactionType === 'out' ? 'Expense' : 'Deposit'} type cannot exceed 25 characters`)
      return
    }

    const currentList = transactionType === 'out' ? expenseTypes : depositTypes

    if (!currentList.includes(newEntryText)) {
      if (transactionType === 'out') {
        setExpenseTypes([...expenseTypes, newEntryText])
      } else {
        setDepositTypes([...depositTypes, newEntryText])
      }

      setSelectedType(newEntryText)
      setNewEntryText('')
      setShowAddNew(false)
      // Only clear error if form has been submitted
      if (formSubmitted) {
        setFormErrors({ ...formErrors, expenseType: false, depositType: false })
      }
    }
  }

  // Form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      // Show appropriate error message based on validation failures
      if (formErrors.capExceeded || (
        transactionType === 'out' &&
        amount &&
        !isNaN(parseFloat(amount)) &&
        parseFloat(amount) > 1000
      )) {
        message.error('Cash out amount cannot exceed PHP 1000')
      } else if (formErrors.balanceExceeded || (
        transactionType === 'out' &&
        amount &&
        !isNaN(parseFloat(amount)) &&
        parseFloat(amount) > parseFloat(currentBalance)
      )) {
        message.error(`Transaction amount exceeds current balance (PHP ${currentBalance})`)
      } else if (formErrors.zeroAmount || (
        amount &&
        !isNaN(parseFloat(amount)) &&
        parseFloat(amount) === 0
      )) {
        message.error('Amount must be greater than 0')
      } else if (formErrors.amount) {
        message.error('Amount is required')
      } else if (formErrors.invoiceNumber) {
        message.error('Invoice number is required for cash out transactions')
      } else if (formErrors.notes) {
        message.error('Description is required')
      }
      return
    }

    try {
      setSubmitting(true)

      // Get user name from context
      let createdByName = userContext?.graphData?.displayName || userContext?.cognitoData?.name

      // Generate nanoid for empty invoice numbers in cash in transactions, use actual value for cash out
      let invoiceNumberForApi
      if (transactionType === 'out') {
        // For cash out, use the actual invoice number (required field)
        invoiceNumberForApi = invoiceNumber.trim()
      } else {
        // For cash in, generate nanoid if invoice number is empty, otherwise use the entered value
        invoiceNumberForApi = (!invoiceNumber || invoiceNumber.trim() === '') ? nanoid(10) : invoiceNumber.trim()
      }

      const formattedData = formatTransactionData({
        transactionType,
        amount,
        invoiceNumber: invoiceNumberForApi,
        expenseType: transactionType === 'out' ? selectedType : undefined,
        depositType: transactionType === 'in' ? selectedType : undefined,
        date: transactionDate,
        time: transactionTime,
        notes: notes.trim(),
        createdBy: createdByName,
        storeId: effectiveStoreId
      })

      // Ensure all files have sanitized names before submitting
      const sanitizedFileList = fileList.map(file => {
        if (file.originFileObj) {
          file.originFileObj = sanitizeFileName(file.originFileObj)
        }
        return file
      })

      const result = await createTransaction(formattedData, sanitizedFileList, instance)
      resetForm()

      // Display API response message instead of static message
      const responseMessage = result?.data?.message ||
        result?.message ||
        (result?.status === 'success' ? 'Transaction created successfully' : 'Transaction created')

      message.success(responseMessage)

      // Call onSuccess callback
      if (onSuccess) {
        onSuccess(result)
      }

      // Close the modal
      onClose()
    } catch (error) {
      console.error('Transaction submission error:', error)

      // Display appropriate error message
      if (error.response) {
        const errorMessage = error.response.data?.message || error.response.data?.error || error.response.statusText
        message.error(`Failed to create transaction: ${errorMessage}`)
      } else if (error.request) {
        message.error('Content is too large. Please reduce the file size and try again.')
      } else {
        message.error('Failed to create transaction: ' + (error.message || 'Unknown error'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  // Render dropdown with "Add new" option
  const typeDropdownRender = (menu) => (
    <div className="dropdown-container">
      {loading ? (
        <div style={{ padding: '12px', textAlign: 'center' }}>
          <Spin size="small" tip={`Loading ${transactionType === 'out' ? 'expense' : 'deposit'} types...`} />
        </div>
      ) : (
        <>
          {menu}
          <Divider style={{ margin: '8px 0' }} />
          {!showAddNew ? (
            <div
              style={{ padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#1890ff' }}
              onClick={() => setShowAddNew(true)}
              className="add-new-option"
            >
              <PlusOutlined style={{ marginRight: '8px' }} />
              Add new {transactionType === 'out' ? 'expense' : 'deposit'} type
            </div>
          ) : (
            <div style={{ padding: '8px 12px' }}>
              <Input
                placeholder={`Enter new ${transactionType === 'out' ? 'expense' : 'deposit'} type`}
                value={newEntryText}
                onChange={(e) => {
                  // Limit the length to 25 characters
                  if (e.target.value.length <= 25) {
                    setNewEntryText(e.target.value)
                  }
                }}
                onPressEnter={handleAddNewType}
                autoFocus
                style={{ marginBottom: '8px', width: '100%' }}
                maxLength={25}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="primary"
                  size="small"
                  onClick={handleAddNewType}
                  style={{ marginRight: '8px' }}
                >
                  Add
                </Button>
                <Button
                  type="default"
                  size="small"
                  onClick={() => setShowAddNew(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )

  return (
    <Modal
      title={<div className="modal-title">Petty Cash Transaction</div>}
      open={visible}
      onCancel={() => { resetForm(); onClose() }}
      footer={null}
      width={520}
      centered
      className="petty-cash-transaction-modal"
    >
      <Form form={form} layout="vertical" className="transaction-form">
        {/* Date and Time Fields */}
        <div className="date-time-section">
          <Form.Item
            label={<span className="highlighted-label">Date *</span>}
            className="date-picker-item"
            validateStatus={formSubmitted && formErrors.date ? 'error' : ''}
            help={formSubmitted && formErrors.date ? 'Date is required' : null}
          >
            <DatePicker
              value={transactionDate}
              onChange={handleDateChange}
              className="transaction-date"
              placeholder="Select date"
              disabledDate={disabledDate}
              picker="date"
              panelRender={(panel) => {
                // Hide navigation arrows while keeping the month/year display
                const panelWithoutNavArrows = React.cloneElement(panel, {},
                  React.Children.map(panel.props.children, child => {
                    if (child?.props?.className?.includes?.('ant-picker-header')) {
                      return React.cloneElement(child, {
                        children: React.Children.map(child.props.children, headerChild => {
                          if (headerChild?.props?.className?.includes?.('ant-picker-header-view')) {
                            return headerChild
                          }
                          return null
                        })
                      })
                    }
                    return child
                  })
                )
                return panelWithoutNavArrows
              }}
              dropdownClassName="hide-calendar-navigation"
            />
          </Form.Item>
          <Form.Item
            label={<span className="highlighted-label">Time *</span>}
            className="time-picker-item"
            validateStatus={formSubmitted && formErrors.time ? 'error' : ''}
            help={formSubmitted && formErrors.time ? 'Time is required' : null}
          >
            <TimePicker
              value={transactionTime}
              onChange={(time) => {
                setTransactionTime(time)
                if (formSubmitted) {
                  setFormErrors({ ...formErrors, time: false })
                }
              }}
              use12Hours
              format="h:mm A"
              className="transaction-time"
              placeholder="Select time"
              disabledTime={disabledTime}
            />
          </Form.Item>
        </div>

        {/* Transaction Type Selection */}
        <Form.Item
          label={<span className="highlighted-label">Transaction Type *</span>}
          className="transaction-type-container"
        >
          <Radio.Group
            value={transactionType}
            onChange={handleTransactionTypeChange}
            className="transaction-type-radio"
          >
            <Radio value="out">Cash Out</Radio>
            <Radio value="in">Cash In</Radio>
          </Radio.Group>
        </Form.Item>

        {/* Transaction Type Options */}
        <Form.Item
          label={<span className="highlighted-label">{transactionType === 'out' ? 'Expense' : 'Deposit'} Type *</span>}
          validateStatus={formSubmitted && formErrors[transactionType === 'out' ? 'expenseType' : 'depositType'] ? 'error' : ''}
          help={formSubmitted && formErrors[transactionType === 'out' ? 'expenseType' : 'depositType'] ?
            `${transactionType === 'out' ? 'Expense' : 'Deposit'} type is required` : null}
        >
          <Select
            placeholder={`Select or search ${transactionType === 'out' ? 'expense' : 'deposit'} type`}
            value={selectedType}
            onChange={handleTypeChange}
            dropdownRender={typeDropdownRender}
            className="expense-type-select"
            showSearch
            listHeight={240}
            onDropdownVisibleChange={(open) => {
              if (!open) {
                setShowAddNew(false)
                setNewEntryText('')
              }
            }}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {(transactionType === 'out' ? expenseTypes : depositTypes).map(type => (
              <Option key={type} value={type}>{type}</Option>
            ))}
          </Select>
        </Form.Item>

        {/* Invoice Number - Mandatory for cash out, optional for cash in */}
        <Form.Item
          label={<span className="highlighted-label">Invoice Number {transactionType === 'out' ? '*' : ''}</span>}
          validateStatus={invoiceNumberError || (formSubmitted && formErrors.invoiceNumber) ? 'error' : ''}
          help={invoiceNumberError || (formSubmitted && formErrors.invoiceNumber ? 'Invoice number is required for cash out transactions' : null)}
        >
          <Input
            placeholder={transactionType === 'out' ? 'Enter invoice number' : 'Enter invoice number (optional)'}
            value={invoiceNumber}
            onChange={e => {
              const value = e.target.value

              // Check for invalid characters
              if (value && !/^[a-zA-Z0-9-_.]*$/.test(value)) {
                setInvoiceNumberError('Special characters are not allowed')
              } else if (value.length > 20) {
                setInvoiceNumberError('Invoice number cannot exceed 20 characters')
              } else {
                // Clear error when input is valid
                setInvoiceNumberError('')
              }

              // Always update the value - we'll show an error but still allow typing
              setInvoiceNumber(value)

              // Clear form error if form has been submitted and field is now valid
              if (formSubmitted) {
                if (transactionType === 'out' && (!value || value.trim() === '')) {
                  setFormErrors({ ...formErrors, invoiceNumber: true })
                } else {
                  setFormErrors({ ...formErrors, invoiceNumber: false })
                }
              }
            }}
            maxLength={30} // Set higher than 20 to capture input but still show error
          />
        </Form.Item>

        {/* Amount */}
        <>
          <div className="amount-label-row">
            <span className="amount-label">Amount *</span>
            <span className="balance-text">Balance: PHP {currentBalance}</span>
          </div>

          <Form.Item
            validateStatus={(amountError || (formSubmitted && formErrors.amount) || formErrors.balanceExceeded || formErrors.zeroAmount || formErrors.capExceeded) ? 'error' : ''}
            help={amountError || (formSubmitted && formErrors.amount ? 'Amount is required' : null)}
          >
            <Input
              prefix="PHP"
              placeholder="0.00"
              value={amount}
              onChange={handleAmountChange}
            />
          </Form.Item>
        </>

        {/* Description - Now mandatory */}
        <Form.Item
          label={<span className="highlighted-label">Description *</span>}
          validateStatus={formSubmitted && formErrors.notes ? 'error' : ''}
          help={formSubmitted && formErrors.notes ? 'Description is required' : null}
        >
          <TextArea
            placeholder="Enter transaction details"
            value={notes}
            onChange={e => {
              if (e.target.value.length <= 100) {
                setNotes(e.target.value)

                // Clear form error if form has been submitted and field is now valid
                if (formSubmitted) {
                  if (!e.target.value || e.target.value.trim() === '') {
                    setFormErrors({ ...formErrors, notes: true })
                  } else {
                    setFormErrors({ ...formErrors, notes: false })
                  }
                }
              }
            }}
            autoSize={{ minRows: 4, maxRows: 6 }}
            maxLength={100}
          />
          <div className="character-count" style={{ textAlign: 'right', color: notes.length >= 100 ? '#ff4d4f' : '#8c8c8c' }}>
            {notes.length}/100
          </div>
        </Form.Item>

        {/* Attachments with filename space removal */}
        <Form.Item label={<span className="highlighted-label">Attachments (Images & PDFs Only)</span>}>
          <Dragger
            multiple
            fileList={fileList}
            onChange={handleFileChange}
            onRemove={(file) => {
              const newFileList = fileList.filter(item => item.uid !== file.uid)
              setFileList(newFileList)
              return true
            }}
            beforeUpload={(file) => {
              // First, sanitize the filename (remove spaces)
              const sanitizedFile = sanitizeFileName(file)

              // Check file type
              const isImage = sanitizedFile.type?.startsWith('image/')
              const isPdf = sanitizedFile.type === 'application/pdf'
              const isValidFile = isImage || isPdf

              if (!isValidFile) {
                message.error(`${sanitizedFile.name} is not an image or PDF file`)
              }

              // Return false to prevent auto-upload
              return isValidFile ? false : Upload.LIST_IGNORE
            }}
            className="drag-upload"
            showUploadList={false}
            accept="image/*,.pdf"
            disabled={submitting}
          >
            <p className="ant-upload-drag-icon"></p><p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Click or drag image or PDF files here to upload</p>
            <p className="ant-upload-hint">Supports multiple image files (JPG, PNG, etc.) and PDF documents</p>
          </Dragger>
          {fileList.length > 0 && (
            <div className="file-list">
              {fileList.map(file => (
                <div key={file.uid} className="file-item">
                  <span>{file.name}</span>
                  <DeleteOutlined onClick={() => {
                    const newFileList = fileList.filter(item => item.uid !== file.uid)
                    setFileList(newFileList)
                  }} />
                </div>
              ))}
            </div>
          )}
        </Form.Item>

        {/* Form Actions */}
        <div className="modal-actions">
          <Button
            type="primary"
            onClick={handleSubmit}
            className="submit-btn"
            loading={submitting}
            disabled={
              submitting ||
              // Check for any error messages
              amountError !== '' ||
              invoiceNumberError !== '' ||
              // Check for any form validation errors
              Object.values(formErrors).some(error => error) ||
              // Check for required fields
              !amount ||
              !selectedType ||
              !transactionDate ||
              !transactionTime ||
              !notes ||
              (transactionType === 'out' && !invoiceNumber) ||
              // Check for specific validation cases
              (amount &&
                !isNaN(parseFloat(amount)) &&
                (parseFloat(amount) === 0 ||
                  (transactionType === 'out' && parseFloat(amount) > 1000) ||
                  (transactionType === 'out' && parseFloat(amount) > parseFloat(currentBalance))))
            }
          >
            Submit
          </Button>

        </div>
      </Form>
    </Modal>
  )
}

export default TransactionModal